/**
 * V2 — SimulationPublic — Tunnel public adaptatif "Mon projet est-il finançable ?"
 *
 * - Mobile-first, vouvoiement, une question par écran, parcours dépendant de la maturité
 * - Aucun montant de financement affiché (l'API publique ne les renvoie pas)
 * - Lead + Dossier CRM créés à l'étape coordonnées, puis indice → CTA "Être rappelé par {public_full_name}"
 * - Qualification IA facultative, uniquement après confirmation du rappel
 * - Lot 2A (minimisation) : le brouillon (réponses + coordonnées) vit UNIQUEMENT en mémoire. Conséquence assumée :
 *   fermer l'onglet ou recharger la page avant la fin oblige à recommencer le questionnaire.
 *   Seuls les identifiants techniques (session anonyme, simulation_id) restent en sessionStorage pour l'idempotence.
 *   Seule la clé historique exacte localStorage « cf:sim:draft » est supprimée à l'ouverture ; aucun autre stockage n'est touché.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { emptyDraft, CRENEAUX, CONSENT_TEXT_VERSION, ATTRIBUTION_KEYS } from "@/components/simulation/simConstants";
import { nextStep, prevStep, progress, buildInputs } from "@/components/simulation/simEngine";
import { StepMaturity, StepDestination, StepTypeBien, StepLocalisationProjet, StepPrix, StepTravaux, StepDpe, StepDatesCompromis, StepApport } from "@/components/simulation/SimStepsProjet";
import { StepFoyer, StepProfil, StepRevenus, StepAutresRevenus } from "@/components/simulation/SimStepsEmprunteur";
import { StepLogement, StepCredits, StepPension } from "@/components/simulation/SimStepsCharges";
import { StepLanding, StepComputing, StepCoords, StepIndice, StepCallbackConfirm, StepCallbackDone, StepQualifEnd } from "@/components/simulation/SimResultV2";
import QualifChat from "@/components/simulation/SimQualifChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_DRAFT_KEY = "cf:sim:draft";  // clé historique exacte — aucune autre clé cf:sim* n'est touchée
const SESSION_KEY = "cf:sim:session:v2";
const GRAD = "var(--cf-gradient)";

function getOrCreateSession() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = crypto?.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return String(Date.now()); }
}

const TECH_STEPS = new Set(["landing", "computing", "coords", "result", "callback_confirm", "callback_done", "qualif", "qualif_end"]);

export default function SimulationPublic() {
  const { slug: slugParam } = useParams();
  const slug = slugParam || "teddy";
  const [searchParams] = useSearchParams();
  // Attribution conservée en mémoire uniquement (pas de persistance navigateur — décision lot 1) ; transmise au serveur sur chaque événement.
  const utm = useMemo(() => Object.fromEntries(ATTRIBUTION_KEYS.map((k) => [k, searchParams.get(k) || null])), [searchParams]);
  const ref = searchParams.get("ref") || null;  // V9-B1 — code de recommandation apporteur (opaque, résolu côté serveur)

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [draft, setDraft] = useState(() => {
    try { localStorage.removeItem(LEGACY_DRAFT_KEY); } catch { /* noop */ }
    return emptyDraft();
  });
  const [result, setResult] = useState(null);
  const [simulationId, setSimulationId] = useState(() => { try { return sessionStorage.getItem("cf:sim:simid:v2") || null; } catch { return null; } });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [creneau, setCreneau] = useState(null);
  const sessionId = useRef(getOrCreateSession()).current;
  const firedEvents = useRef(new Set());

  useEffect(() => { try { if (simulationId) sessionStorage.setItem("cf:sim:simid:v2", simulationId); } catch { /* noop */ } }, [simulationId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/simulator/config`, { params: { slug } });
        setConfig(r.data);
        if (!firedEvents.current.has("simulation_viewed")) {
          firedEvents.current.add("simulation_viewed");
          axios.post(`${API}/public/simulator/event`, { slug, event: "simulation_viewed", session_id: sessionId, utm, ref, meta: { schema_version: 2 } }).catch(() => {});
        }
      } catch { setConfigError(true); setConfig({ display_name: "—" }); }
    })();
  }, [slug, sessionId, utm, ref]);

  const patch = useCallback((p) => setDraft((d) => ({ ...d, ...p })), []);
  const patchNested = useCallback((key, p) => setDraft((d) => ({ ...d, [key]: { ...(d[key] || {}), ...p } })), []);
  const go = useCallback((stepKey) => setDraft((d) => ({ ...d, stepKey })), []);

  const fireEvent = useCallback((event, meta) => {
    const key = `${event}:${meta?.step || ""}`;
    if (firedEvents.current.has(key)) return;
    firedEvents.current.add(key);
    axios.post(`${API}/public/simulator/event`, { slug, event, session_id: sessionId, step: meta?.step, utm, ref, simulation_id: meta?.simulation_id, meta: { ...meta, schema_version: 2 } }).catch(() => {});
  }, [slug, sessionId, utm, ref]);

  const next = useCallback(() => {
    setDraft((d) => {
      const n = nextStep(d);
      if (d.stepKey === "landing") fireEvent("simulation_started", { step: n });
      else fireEvent("step_completed", { step: d.stepKey });
      return { ...d, stepKey: n };
    });
  }, [fireEvent]);
  const prev = useCallback(() => setDraft((d) => ({ ...d, stepKey: prevStep(d) })), []);

  const resetTunnel = () => {
    try { sessionStorage.removeItem("cf:sim:simid:v2"); } catch { /* noop */ }
    setDraft({ ...emptyDraft(), stepKey: "maturity" }); setResult(null); setSimulationId(null); setError(null); setCreneau(null);
    firedEvents.current = new Set();
  };

  const stepKey = draft.stepKey;

  /* Écran "computing" → transition automatique vers les coordonnées */
  useEffect(() => {
    if (stepKey !== "computing") return;
    const t = setTimeout(() => go("coords"), 1200);
    return () => clearTimeout(t);
  }, [stepKey, go]);

  const submitLead = async () => {
    if (submitting) return;
    const c = draft.coords;
    if (!c.prenom || !c.nom || !c.telephone || !c.email || !c.consent) { setError("Merci de renseigner l'ensemble des informations et de valider le consentement."); return; }
    setSubmitting(true); setError(null);
    try {
      const r = await axios.post(`${API}/public/simulator/lead`, { slug, inputs: buildInputs(draft), coords: { prenom: c.prenom, nom: c.nom, telephone: c.telephone, email: c.email, consent: c.consent, consent_text_version: CONSENT_TEXT_VERSION }, utm, ref, session_id: sessionId });
      setSimulationId(r.data.simulation_id); setResult(r.data.result);
      fireEvent("simulation_completed", { step: "coords" });
      go("result");
    } catch (e) {
      const detail = e?.response?.status === 400 && typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null;
      setError(detail || "Une erreur est survenue. Vérifiez les informations et réessayez.");
    }
    finally { setSubmitting(false); }
  };

  const confirmCallback = async ({ creneau: cr, telephone }) => {
    if (!simulationId || submitting) return;
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/simulator/callback`, { slug, simulation_id: simulationId, creneau: cr, channel: "phone", telephone_corrige: telephone || null, utm });
      if (telephone) patchNested("coords", { telephone });
      setCreneau(cr); go("callback_done");
    } catch (e) {
      const detail = e?.response?.status === 400 && typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null;
      setError(detail || "Impossible d'enregistrer la demande. Réessayez dans un instant.");
    }
    finally { setSubmitting(false); }
  };

  if (!config) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white"><div className="animate-pulse text-slate-400 text-sm">Chargement…</div></div>;
  if (configError) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-6" data-testid="sim-slug-not-found">
      <div className="max-w-sm text-center"><div className="text-5xl mb-4" aria-hidden="true">🔗</div>
        <h1 className="font-extrabold text-slate-900 text-[22px] leading-tight mb-2">Ce lien de simulation n'est plus disponible</h1>
        <p className="text-slate-600 text-[14px]">Vérifiez que vous utilisez bien le lien que votre courtier vous a partagé.</p></div>
    </div>
  );

  const prog = progress(draft);
  const isQuestion = !TECH_STEPS.has(stepKey);
  const common = { draft, patch, patchNested, next };
  const creneauLabel = CRENEAUX.find((c) => c.key === creneau)?.label;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white sim-root" data-testid="sim-root">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-100" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Avatar name={config.display_name} photoUrl={config.photo_url} />
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-slate-900 text-[15px] leading-tight truncate">{config.public_full_name || config.display_name}</div>
            <div className="text-[11px] text-slate-500 leading-tight truncate">{config.subtitle}</div>
          </div>
        </div>
        {isQuestion && (
          <div className="max-w-lg mx-auto px-4 pb-2">
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={prog.pct} data-testid="sim-progress">
              <div className="h-full rounded-full transition-all" style={{ width: `${prog.pct}%`, background: GRAD }} />
            </div>
          </div>
        )}
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 pb-28" data-testid={`sim-step-${stepKey}`}>
        {stepKey === "landing" && <StepLanding config={config} onStart={next} />}
        {stepKey === "maturity" && <StepMaturity {...common} />}
        {stepKey === "destination" && <StepDestination {...common} />}
        {stepKey === "type_bien" && <StepTypeBien {...common} />}
        {stepKey === "localisation_projet" && <StepLocalisationProjet {...common} />}
        {stepKey === "prix_cible" && <StepPrix {...common} cible />}
        {stepKey === "prix_bien" && <StepPrix {...common} />}
        {stepKey === "travaux" && <StepTravaux {...common} />}
        {stepKey === "dpe" && <StepDpe {...common} />}
        {stepKey === "dates_compromis" && <StepDatesCompromis {...common} />}
        {stepKey === "foyer" && <StepFoyer {...common} />}
        {stepKey === "profil_e1" && <StepProfil {...common} idx={1} />}
        {stepKey === "revenus_e1" && <StepRevenus {...common} idx={1} />}
        {stepKey === "profil_e2" && <StepProfil {...common} idx={2} />}
        {stepKey === "revenus_e2" && <StepRevenus {...common} idx={2} />}
        {stepKey === "autres_revenus" && <StepAutresRevenus {...common} />}
        {stepKey === "logement" && <StepLogement {...common} />}
        {stepKey === "credits" && <StepCredits {...common} />}
        {stepKey === "pension" && <StepPension {...common} />}
        {stepKey === "apport" && <StepApport {...common} />}
        {stepKey === "computing" && <StepComputing />}
        {stepKey === "coords" && <StepCoords coords={draft.coords} onChange={(p) => patchNested("coords", p)} onSubmit={submitLead} submitting={submitting} error={error} />}
        {stepKey === "result" && result && <StepIndice result={result} prenom={draft.coords.prenom} config={config} onCallback={() => go("callback_confirm")} onReset={resetTunnel} />}
        {stepKey === "result" && !result && <div className="pt-10 text-center text-slate-500 text-sm" data-testid="sim-result-expired">Cette session a expiré. <button type="button" onClick={resetTunnel} className="text-[color:var(--cf-primary)] font-bold underline">Refaire une simulation</button></div>}
        {stepKey === "callback_confirm" && <StepCallbackConfirm coords={draft.coords} config={config} onConfirm={confirmCallback} onBack={() => go("result")} submitting={submitting} error={error} />}
        {stepKey === "callback_done" && <StepCallbackDone prenom={draft.coords.prenom} config={config} creneauLabel={creneauLabel} onStartQualif={simulationId ? () => go("qualif") : null} />}
        {stepKey === "qualif" && simulationId && <QualifChat simulationId={simulationId} prenom={draft.coords.prenom} config={config} onCompleted={() => go("qualif_end")} onFallback={() => go("qualif_end")} />}
        {stepKey === "qualif_end" && <StepQualifEnd config={config} />}

        {isQuestion && (
          <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-100" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <button type="button" onClick={prev} className="px-4 py-2 rounded-full text-slate-600 text-[13px] font-bold hover:bg-slate-100 active:scale-95 transition" data-testid="sim-prev-btn">← Précédent</button>
              <div className="text-[11px] text-slate-400 font-medium" data-testid="sim-step-counter">Question {prog.index}/{prog.total}</div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Avatar({ name, photoUrl }) {
  if (photoUrl) return <img src={photoUrl} alt={name} className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow" />;
  const initials = (name || "C").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-extrabold text-sm ring-2 ring-white shadow" style={{ background: "var(--cf-gradient)" }} aria-label={name}>{initials}</div>;
}
